/**
 * Phase 12 — Usage section for the Subscription settings sub-page.
 *
 * Ported from the agency-portal Billing page's detail-list logic. Branches on
 * `usage.type`:
 *   - 'agency'     -> "Active connected clients: N" + list of shops with
 *                     activation dates.
 *   - 'enterprise' -> "Active seats: N" + employee list with activation dates.
 *
 * Read-only display: the parent page still owns Stripe subscription management.
 * Gated by `<RoleGate capability="billing.view">` from the caller.
 *
 * Journey C — migrated off MUI (Paper/Table/Chip/Alert/CircularProgress) to the
 * legacy SCSS card + table shell so it matches the Audit Log table and the rest
 * of the agency portal. Status badge uses the shared `StatusPill`; the spinner
 * is the shared `LoadingSpinner`; errors use the standard inline-error pattern.
 *
 * Reuses `src/lib/api/billing/billing-api.ts` (Phase 5-B).
 */
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store/store';
import {
  BilledClient,
  BilledSeat,
  UsageResponse,
  getClients,
  getSeats,
  getUsage,
} from '../../../lib/api/billing/billing-api';
import { useBrandLabels } from '../../../lib/identity/brand-mapping';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import StatusPill from '../../../ui/statusPill/StatusPill';
import NoAccessNotice from '../../../components/noAccess/NoAccessNotice';
import isAccessDeniedError from '../../../lib/helper-functions/isAccessDeniedError';

function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const BillingUsageSection = () => {
  const { t } = useTranslation();
  const labels = useBrandLabels();
  // Only attempt to load when an agency access token exists — sellers without
  // an agency identity get an empty section instead of a 401 spam loop.
  const agencyToken = useSelector(
    (s: RootState) => s.auth?.agency?.accessToken ?? ''
  );
  // Guards use the boolean, but effects must key on the token VALUE: the
  // admin "login as seller" handoff swaps the agency session mid-page, and a
  // boolean dep would skip the refetch and keep stale wrong-org data.
  const hasAgencyToken = Boolean(agencyToken);

  const [loading, setLoading] = useState<boolean>(hasAgencyToken);
  const [error, setError] = useState<string | null>(null);
  // Usage is owner/manager data: other roles get 401/403 and see the
  // no-access message, not the raw "forbidden" text.
  const [accessDenied, setAccessDenied] = useState(false);
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [clients, setClients] = useState<BilledClient[]>([]);
  const [seats, setSeats] = useState<BilledSeat[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAccessDenied(false);
    try {
      const u = await getUsage();
      setUsage(u);
      if (u.type === 'agency') {
        const list = await getClients();
        setClients(list);
        setSeats([]);
      } else {
        const list = await getSeats();
        setSeats(list);
        setClients([]);
      }
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[BillingUsageSection] load failed', err);
      if (isAccessDeniedError(err)) {
        setAccessDenied(true);
        return;
      }
      setError(err?.message || 'Failed to load usage data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasAgencyToken) {
      load();
    }
  }, [agencyToken, load]);

  if (!hasAgencyToken) {
    // No agency-portal session — usage doesn't apply to this account shape.
    return null;
  }

  if (accessDenied) {
    return (
      <div className='billingUsage'>
        <div className='billingUsage__header'>
          <h3 className='billingUsage__title'>{t('Usage')}</h3>
        </div>
        <NoAccessNotice />
      </div>
    );
  }

  return (
    <div className='billingUsage'>
      <div className='billingUsage__header'>
        <h3 className='billingUsage__title'>{t('Usage')}</h3>
        {usage?.type && <StatusPill tone='info'>{usage.type}</StatusPill>}
      </div>

      {error && (
        <div className='billingUsage__error' role='alert'>
          <span>{error}</span>
          <button
            type='button'
            className='billingUsage__error--close'
            onClick={() => setError(null)}
            aria-label={t('Close')}
          >
            ×
          </button>
        </div>
      )}

      {loading ? (
        <div className='billingUsage__loading'>
          <LoadingSpinner customLoadingMsg={t('Loading')} />
        </div>
      ) : usage ? (
        <div>
          <p className='billingUsage__summary'>
            {usage.type === 'agency' ? (
              <>
                {t('ActiveConnected')} {labels.shopLowerPlural}:{' '}
                <strong>{usage.count}</strong>
              </>
            ) : (
              <>
                {t('ActiveSeats')}: <strong>{usage.count}</strong>
              </>
            )}
          </p>

          {usage.type === 'agency' ? (
            clients.length === 0 ? (
              <div className='billingUsage__empty'>
                {`No connected ${labels.shopLowerPlural} this month.`}
              </div>
            ) : (
              <div className='billingUsage__table'>
                <div className='billingUsage__head'>
                  <div>{`${labels.shopSingular} slug`}</div>
                  <div>{t('Activated')}</div>
                </div>
                {clients.map((c) => (
                  <div className='billingUsage__row' key={c.slug}>
                    <div>{c.slug}</div>
                    <div className='billingUsage__cellMuted'>
                      {formatDate(c.activated_at)}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : seats.length === 0 ? (
            <div className='billingUsage__empty'>
              No active seats this month.
            </div>
          ) : (
            <div className='billingUsage__table'>
              <div className='billingUsage__head'>
                <div>{t('Email')}</div>
                <div>{t('Activated')}</div>
              </div>
              {seats.map((s) => (
                <div className='billingUsage__row' key={s.employee_id}>
                  <div>{s.email}</div>
                  <div className='billingUsage__cellMuted'>
                    {formatDate(s.activated_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className='billingUsage__empty'>No usage data available.</div>
      )}
    </div>
  );
};

export default BillingUsageSection;
